import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trainingApi } from '@/lib/apiClient';
import { StateView } from '@/components';
import { fmtInstantDate } from '@/lib/dates';
import { TRAINING } from '../training/content';
import {
  buildRows, summarise, byProgressThenName, deadlineSentence,
  exportManagerXlsx, exportManagerDocx, exportSummaryXlsx, exportSummaryDocx,
  type TrainingRow, type TrainingState,
} from './trainingReport';

/**
 * Who has worked through the training guide.
 *
 * ⚠️ SELF-CONTAINED STYLES. This tab used to borrow `pw-card`, `qq-table` and
 * the button shape now called `ew-btn-link`, which are declared in <style>
 * blocks inside ProjectWorkspace, QaqcTab and OverviewEdit. A <style> block is
 * an ordinary GLOBAL stylesheet, but it only exists while its component is
 * mounted — and none of those three mount on the Admin page, so the classes
 * resolved to nothing and the table rendered with no header rule, no row
 * borders and no card chrome. Everything this file needs is in `css` below; do
 * not reach for a class defined in another module's <style>. Genuinely shared
 * classes belong in styles/tokens.css, which is always loaded.
 *
 * ⚠️ THE DENOMINATOR IS PER PERSON, and that is the whole difficulty. The guide
 * is access-filtered, so "12 of 40" is meaningless unless the 40 is the topics
 * THAT PERSON is shown — a PM is not incomplete for never reading the admin
 * track they cannot open. That target comes from `targetTopics`, shared with
 * the reader's own count on TrainingPage. They were written separately once and
 * both forgot the appendix, so ticking a module-reference card moved neither
 * number (review, #283). The row model now lives in ./trainingReport so the
 * exports cannot drift from the table either.
 *
 * ⚠️ The roles and permissions come from the TRAINING endpoint, not GET /users,
 * which returns id/name/email only. Using it made every person evaluate as
 * unprivileged: the Roles column read "—" for everyone and every gated topic
 * fell out of every denominator.
 *
 * ⚠️ STALE TICKS ARE COUNTED SEPARATELY. A tick recorded against an older guide
 * revision still counts as done — the person did read it — but is called out,
 * because the text may have changed since. Nothing is ever auto-un-ticked.
 */

const css = `
  .tp-card { background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); }
  .tp-hd {
    display: flex; align-items: flex-start; justify-content: space-between; gap: var(--ew-s-4);
    flex-wrap: wrap; padding: 14px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100);
  }
  .tp-hd h2 {
    margin: 0; font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1 var(--ew-font);
    letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500);
  }
  .tp-note { font: var(--ew-fw-regular) var(--ew-fs-meta)/1.5 var(--ew-font); color: var(--ew-n-500); }
  /* Same sentence the exports print, so the screen and the file cannot
     disagree about the date people were given. */
  .tp-deadline {
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1.4 var(--ew-font); color: var(--ew-dark);
    margin-top: 8px; display: flex; align-items: baseline; gap: 7px;
  }
  .tp-deadline::before {
    content: ''; width: 8px; height: 8px; border-radius: 50%;
    background: var(--ew-performance); flex: 0 0 auto; transform: translateY(-1px);
  }

  /* Headline tiles. Four numbers that answer "do I need to chase anyone" before
     the reader has to parse a 50-row table. */
  .tp-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); border-bottom: 1px solid var(--ew-n-100); }
  .tp-kpi { padding: 14px var(--ew-s-4); border-left: 1px solid var(--ew-n-100); }
  .tp-kpi:first-child { border-left: 0; }
  .tp-kpi-n {
    font: var(--ew-fw-bold) 28px/1 var(--ew-font); font-variant-numeric: tabular-nums;
    color: var(--ew-dark); display: block;
  }
  .tp-kpi-l {
    font: var(--ew-fw-medium) var(--ew-fs-meta)/1.3 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); margin-top: 6px; display: block;
  }
  .tp-kpi-sub { font: var(--ew-fw-regular) var(--ew-fs-meta)/1.3 var(--ew-font); color: var(--ew-n-400); margin-top: 3px; display: block; }

  .tp-tools {
    display: flex; align-items: center; gap: var(--ew-s-2); flex-wrap: wrap;
    padding: 12px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100);
  }
  .tp-chip {
    border: 1px solid var(--ew-n-200); background: #fff; border-radius: var(--ew-r-pill);
    padding: 5px 12px; cursor: pointer; white-space: nowrap;
    font: var(--ew-fw-medium) var(--ew-fs-meta)/1.4 var(--ew-font); color: var(--ew-n-600);
    transition: background var(--ew-dur-fast) var(--ew-ease), border-color var(--ew-dur-fast) var(--ew-ease);
  }
  .tp-chip:hover { background: var(--ew-n-50); }
  .tp-chip[aria-pressed='true'] { background: var(--ew-dark); border-color: var(--ew-dark); color: #fff; }
  .tp-search {
    padding: 6px 10px; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-md);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); min-width: 180px; flex: 1 1 180px; max-width: 260px;
  }
  .tp-search:focus { outline: 2px solid var(--ew); outline-offset: 1px; border-color: var(--ew); }

  .tp-exports { display: flex; align-items: center; gap: var(--ew-s-2); flex-wrap: wrap; }
  .tp-exp-label { font: var(--ew-fw-medium) var(--ew-fs-meta)/1.4 var(--ew-font); color: var(--ew-n-500); }
  .tp-btn {
    border: 1px solid var(--ew-n-300); background: #fff; border-radius: var(--ew-r-md);
    padding: 6px 11px; cursor: pointer; white-space: nowrap;
    font: var(--ew-fw-medium) var(--ew-fs-meta)/1.4 var(--ew-font); color: var(--ew-dark);
    transition: background var(--ew-dur-fast) var(--ew-ease);
  }
  .tp-btn:hover { background: var(--ew-n-50); }
  .tp-btn:disabled { opacity: 0.5; cursor: default; }

  /* ⚠️ This wrapper owns BOTH scroll axes on purpose, and it must keep a height
     bound. A position:sticky header cell resolves against the nearest
     SCROLL CONTAINER, not the viewport — and the shared .ew-table-scroll this
     used to use only scrolls sideways, so its height was always its content
     height and the sticky header below had nothing to pin against. With 73
     people the column headings simply scrolled away. Same reasoning, and the
     same 72vh, as the Manpower schedule grid. */
  .tp-scroll { overflow: auto; max-height: 72vh; -webkit-overflow-scrolling: touch; }

  /* ⚠️ FIXED layout, not auto. On auto the seven columns each took their
     content width, the total ran past the card, and the horizontal scroller
     silently ate the RIGHTMOST column — "Last activity" read "Aug 17," with
     the year over the edge. Fixed makes the widths below add up to exactly the
     card, so the date is always whole and only genuinely narrow screens
     scroll. Every column that can be squeezed is; the date column is not, so
     it can never be the one that loses.

     ⚠️ THE BUDGET IS 960px, NOT THE WINDOW. AdminPage wraps every tab in a
     maxWidth:960 div, so this table never gets the width of a wide screen:
     958px inside the card's borders, and ~943 once the wrapper above shows its
     vertical scrollbar. The six fixed columns below therefore total 710, which
     leaves Person ~230 here and still fits the narrowest desktop card (853px at
     a 901px window, the width just before the card reflow takes over). Widen
     one of them and you re-open the bug — check the sum against 943, and
     against the cap in AdminPage.tsx, not against your monitor. */
  .tp-table {
    width: 100%; min-width: 820px; table-layout: fixed; border-collapse: collapse;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font);
  }
  /* Person takes whatever is left over — it is the column with the most to say
     and the only one that reads better wide. */
  .tp-table th:nth-child(2) { width: 150px; }  /* Roles */
  .tp-table th:nth-child(3) { width: 120px; }  /* Track counted */
  .tp-table th:nth-child(4) { width: 150px; }  /* Progress */
  .tp-table th:nth-child(5) { width: 100px; }  /* Older revision */
  .tp-table th:nth-child(6) { width: 70px; }   /* Notes */
  .tp-table th:nth-child(7) { width: 120px; }  /* Last activity — fits "Aug 17, 2026" whole */
  .tp-table th {
    /* Headers wrap onto a second line rather than force the column wider than
       the width above — a nowrap header would spill out of a fixed cell. */
    text-align: left; padding: 10px var(--ew-s-3); background: var(--ew-n-50);
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); border-bottom: 1px solid var(--ew-n-200);
    position: sticky; top: 0; z-index: 2;
    /* border-collapse resolves the header's bottom border onto the first BODY
       row, which then slides under the pinned header and takes the rule with
       it. Painting the same line as an inset shadow keeps it with the header. */
    box-shadow: inset 0 -1px 0 var(--ew-n-200);
  }
  .tp-table th button {
    background: none; border: 0; padding: 0; cursor: pointer; font: inherit; color: inherit;
    letter-spacing: inherit; text-transform: inherit;
  }
  /* Follows aria-sort on the <th> — see the Th component for why it lives
     there and not on the button. */
  .tp-table th[aria-sort] button { color: var(--ew-dark); }
  .tp-table td { padding: 10px var(--ew-s-3); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: middle; }
  .tp-table tbody tr:hover td { background: var(--ew-n-50); }
  .tp-table tbody tr.tp-open td { background: var(--ew-n-50); }
  .tp-muted { color: var(--ew-n-500); }
  .tp-num { font-variant-numeric: tabular-nums; white-space: nowrap; }
  .tp-name { font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1.3 var(--ew-font); }
  /* An email address offers no break opportunity of its own, so a narrow
     Person column would push it out of the cell instead of wrapping it. */
  .tp-email { font: var(--ew-fw-regular) var(--ew-fs-meta)/1.3 var(--ew-font); color: var(--ew-n-400); margin-top: 2px; overflow-wrap: anywhere; }

  .tp-roles { display: flex; flex-wrap: wrap; gap: 4px; }
  /* ⚠️ These badges must WRAP, not nowrap. A role label is admin-authored and
     validated only up to 50 characters (CreateRoleDto/UpdateRoleDto), and the
     Roles column is now a fixed 150px — under the old auto layout a long label
     widened the column, but a fixed column cannot grow, so a nowrap badge would
     paint straight over Track counted and Progress. overflow-wrap is needed as
     well as the wrap: the built-in labels are single unbroken words
     (ProjectEngineer), which normal wrapping has no opportunity to break.
     Every other nowrap in this table holds generated text of known length — a
     date, a count — so this is the only column where it was a hazard. */
  .tp-role {
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-pill); padding: 1px 8px;
    font: var(--ew-fw-medium) 11px/1.6 var(--ew-font); color: var(--ew-n-600);
    white-space: normal; overflow-wrap: anywhere;
  }

  /* The bar is the point of this column: 20% against 8% is a glance, not a
     subtraction. Pill radius is the one place the scale allows 999px. */
  .tp-prog { min-width: 0; }
  .tp-bar { height: 8px; border-radius: var(--ew-r-pill); background: var(--ew-n-100); overflow: hidden; }
  .tp-bar-fill { height: 100%; border-radius: var(--ew-r-pill); transition: width var(--ew-dur-med) var(--ew-ease); }
  .tp-prog-t { font: var(--ew-fw-medium) var(--ew-fs-meta)/1 var(--ew-font); font-variant-numeric: tabular-nums; color: var(--ew-n-500); margin-top: 5px; }

  .tp-flag {
    display: inline-block; border-radius: var(--ew-r-pill); padding: 2px 9px;
    font: var(--ew-fw-semibold) 11px/1.5 var(--ew-font); white-space: nowrap;
    background: var(--ew-safety-50); color: var(--ew-safety-700); border: 1px solid var(--ew-safety-100);
  }
  .tp-notes-btn {
    background: none; border: 0; padding: 0; cursor: pointer;
    font: var(--ew-fw-medium) var(--ew-fs-meta) var(--ew-font); color: var(--ew-quality);
  }
  .tp-note-row td { background: var(--ew-n-50); padding-top: 4px; padding-bottom: 10px; }
  .tp-note-item { padding: 7px 0; border-bottom: 1px solid var(--ew-n-100); }
  .tp-note-item:last-child { border-bottom: 0; }
  .tp-note-t { font: var(--ew-fw-semibold) var(--ew-fs-meta) var(--ew-font); color: var(--ew-dark); }
  .tp-note-b { font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.5 var(--ew-font); color: var(--ew-n-600); white-space: pre-wrap; margin-top: 3px; }

  .tp-empty { padding: var(--ew-s-6) var(--ew-s-4); text-align: center; color: var(--ew-n-500); font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .tp-foot { padding: 11px var(--ew-s-4); border-top: 1px solid var(--ew-n-100); font: var(--ew-fw-regular) var(--ew-fs-meta)/1.5 var(--ew-font); color: var(--ew-n-500); }

  @media (max-width: 900px) {
    /* As stacked cards there is no header to pin, so the height bound would only
       trap the list in a second scrollbar — let the cards flow with the page. */
    .tp-scroll { max-height: none; overflow: visible; }
    .tp-table thead { display: none; }
    /* ⚠️ The card reflow has to undo BOTH the min-width floor and the fixed
       layout, or the stacked cards inherit a desktop-width table and the
       whole PAGE scrolls sideways again. */
    .tp-table, .tp-table tbody { display: block; width: 100%; min-width: 0; table-layout: auto; }
    .tp-table tbody tr { display: block; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); margin: var(--ew-s-3) var(--ew-s-4); }
    .tp-table td { display: flex; justify-content: space-between; gap: var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); }
    .tp-table td:last-child { border-bottom: 0; }
    .tp-table td::before {
      content: attr(data-label); flex: 0 0 auto;
      font: var(--ew-fw-semibold) 11px/1.6 var(--ew-font); letter-spacing: var(--ew-ls-caption);
      text-transform: uppercase; color: var(--ew-n-500);
    }
    .tp-table td.tp-full { display: block; }
    .tp-table td.tp-full::before { content: none; }
    .tp-prog { min-width: 0; width: 100%; }
  }
`;

// Green once finished, orange while in flight — the accents carry meaning in
// this design system, and "progress" is orange (performance).
const barColour = (r: TrainingRow) =>
  r.state === 'complete' ? 'var(--ew)' : 'var(--ew-performance)';

type Filter = 'all' | TrainingState | 'stale';
const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all',         label: 'Everyone' },
  { key: 'not-started', label: 'Nothing ticked' },
  { key: 'started',     label: 'In progress' },
  { key: 'complete',    label: 'Complete' },
  { key: 'stale',       label: 'Older revision' },
];

type SortKey = 'name' | 'progress' | 'stale' | 'last';

// Each key has one sensible direction, so the headers pick a sort rather than
// toggling one — "least complete first" is not a question anyone asks, and the
// Not started chip answers it anyway.
const SORT_DIR: Record<SortKey, 'ascending' | 'descending'> = {
  name: 'ascending', progress: 'descending', stale: 'descending', last: 'descending',
};

export default function TrainingProgressTab() {
  const progress = useQuery({ queryKey: ['training-progress-all'], queryFn: trainingApi.everyone });
  const [openNotes, setOpenNotes] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('progress');
  const [busy, setBusy] = useState(false);

  const rows = useMemo(() => (progress.data ? buildRows(progress.data) : []), [progress.data]);
  const summary = useMemo(() => summarise(rows), [rows]);

  // The exports always carry EVERYONE, never the filtered view. A report headed
  // "Not started" that silently omitted the people who had finished would be
  // read as the whole company, and the summary counts would contradict it.
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => (filter === 'all' ? true : filter === 'stale' ? r.stale > 0 : r.state === filter))
      .filter((r) => !q || r.name.toLowerCase().includes(q) || (r.email ?? '').toLowerCase().includes(q)
        || r.rolesText.toLowerCase().includes(q))
      .sort((a, b) => {
        if (sort === 'name') return a.name.localeCompare(b.name);
        if (sort === 'stale') return b.stale - a.stale || byProgressThenName(a, b);
        if (sort === 'last') {
          // Never-active goes LAST. Coalescing null to '' and comparing
          // descending puts it first instead, which buries the recent activity
          // this sort exists to surface.
          if (!a.last !== !b.last) return a.last ? -1 : 1;
          return (b.last ?? '').localeCompare(a.last ?? '') || a.name.localeCompare(b.name);
        }
        return byProgressThenName(a, b);
      });
  }, [rows, filter, search, sort]);

  /** The Word builders are async; keep the buttons from being double-fired. */
  const run = async (fn: () => void | Promise<void>) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  // ⚠️ `aria-sort` belongs on the <th>, NOT on the button inside it. The
  // attribute is only supported on the columnheader/rowheader roles — a button
  // has role `button`, so a screen reader silently ignores it and never
  // announces which column is sorted. The button stays the control; the header
  // carries the state.
  const Th = ({ k, label, align }: { k: SortKey; label: string; align?: 'right' }) => (
    <th
      aria-sort={sort === k ? SORT_DIR[k] : undefined}
      style={align === 'right' ? { textAlign: 'right' } : undefined}
    >
      <button onClick={() => setSort(k)}>
        {label}{sort === k ? (SORT_DIR[k] === 'ascending' ? ' ↑' : ' ↓') : ''}
      </button>
    </th>
  );

  return (
    <>
      <style>{css}</style>
      <div className="tp-card">
        <div className="tp-hd">
          <div style={{ maxWidth: 620 }}>
            <h2>Training progress</h2>
            <div className="tp-note" style={{ marginTop: 8 }}>
              How far each person has worked through the guide. The total differs between people
              because the guide only shows what someone can actually reach — a field leader has far
              fewer topics than a PM, and neither is behind for it. Everyone is told, on the page,
              that their progress and notes are visible here.
            </div>
            <div className="tp-deadline">{deadlineSentence()}</div>
          </div>
          <div className="tp-exports">
            <span className="tp-exp-label">Managers</span>
            <button className="tp-btn" disabled={busy || !rows.length} onClick={() => run(() => exportManagerXlsx(rows))}>
              Excel
            </button>
            <button className="tp-btn" disabled={busy || !rows.length} onClick={() => run(() => exportManagerDocx(rows))}>
              Word
            </button>
            <span className="tp-exp-label" style={{ marginLeft: 8 }}>Everyone</span>
            <button className="tp-btn" disabled={busy || !rows.length} onClick={() => run(() => exportSummaryXlsx(rows))}>
              Excel
            </button>
            <button className="tp-btn" disabled={busy || !rows.length} onClick={() => run(() => exportSummaryDocx(rows))}>
              Word
            </button>
          </div>
        </div>

        <StateView query={progress} empty="No users yet.">
          {() => (
            <>
              <div className="tp-kpis">
                <div className="tp-kpi">
                  <span className="tp-kpi-n">{summary.people}</span>
                  <span className="tp-kpi-l">People</span>
                  <span className="tp-kpi-sub">with access to the guide</span>
                </div>
                <div className="tp-kpi">
                  <span className="tp-kpi-n" style={{ color: 'var(--ew)' }}>{summary.complete}</span>
                  <span className="tp-kpi-l">Finished</span>
                  <span className="tp-kpi-sub">every topic they can reach</span>
                </div>
                <div className="tp-kpi">
                  <span className="tp-kpi-n" style={{ color: 'var(--ew-performance)' }}>{summary.started}</span>
                  <span className="tp-kpi-l">In progress</span>
                  <span className="tp-kpi-sub">at least one topic ticked</span>
                </div>
                <div className="tp-kpi">
                  <span className="tp-kpi-n" style={{ color: summary.notStarted ? 'var(--ew-n-400)' : 'var(--ew)' }}>
                    {summary.notStarted}
                  </span>
                  <span className="tp-kpi-l">Nothing ticked</span>
                  <span className="tp-kpi-sub">may still have read it</span>
                </div>
                <div className="tp-kpi">
                  <span className="tp-kpi-n">{summary.pct}%</span>
                  <span className="tp-kpi-l">Company-wide</span>
                  <span className="tp-kpi-sub">{summary.topicsDone} of {summary.topicsTarget} topics</span>
                </div>
              </div>

              <div className="tp-tools">
                {FILTERS.map((f) => {
                  const n = f.key === 'all' ? rows.length
                    : f.key === 'stale' ? rows.filter((r) => r.stale > 0).length
                    : rows.filter((r) => r.state === f.key).length;
                  return (
                    <button
                      key={f.key}
                      className="tp-chip"
                      aria-pressed={filter === f.key}
                      onClick={() => setFilter(f.key)}
                    >
                      {f.label} <span style={{ opacity: 0.65 }}>{n}</span>
                    </button>
                  );
                })}
                <input
                  className="tp-search"
                  placeholder="Search name or role…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search people by name, email or role"
                />
              </div>

              <div className="tp-scroll">
                <table className="tp-table">
                  <thead>
                    <tr>
                      <Th k="name" label="Person" />
                      <th>Roles</th>
                      <th>Track counted</th>
                      <Th k="progress" label="Progress" />
                      <Th k="stale" label="Older revision" />
                      <th>Notes</th>
                      <Th k="last" label="Last activity" />
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((r) => (
                      <Fragment key={r.id}>
                        <tr className={openNotes === r.id ? 'tp-open' : undefined}>
                          <td data-label="Person">
                            <div>
                              <div className="tp-name">{r.name}</div>
                              {r.email && <div className="tp-email">{r.email}</div>}
                            </div>
                          </td>
                          <td data-label="Roles">
                            {r.roles.length
                              ? <span className="tp-roles">{r.roles.map((x) => <span key={x} className="tp-role">{x}</span>)}</span>
                              : <span className="tp-muted">—</span>}
                          </td>
                          {/* Which track the denominator came from. Without it
                              "74%" is unanswerable — the number that prompted
                              this column was someone who had finished every
                              track and was being measured against all six. */}
                          <td data-label="Track counted">
                            <span className="tp-muted">{r.trackLabel}</span>
                          </td>
                          <td data-label="Progress" className="tp-prog tp-full">
                            <div className="tp-bar">
                              <div className="tp-bar-fill" style={{ width: `${r.pct}%`, background: barColour(r) }} />
                            </div>
                            <div
                              className="tp-prog-t"
                              title={`Foundations plus the ${r.trackLabel} track, filtered to what they can reach. Other tracks and the screen-by-screen appendix are not counted.`}
                            >
                              {r.done} of {r.total} · {r.pct}%
                            </div>
                          </td>
                          <td data-label="Older revision">
                            {r.stale > 0
                              ? <span className="tp-flag" title={`Ticked before revision ${TRAINING.revision}; the wording may have changed since.`}>{r.stale}</span>
                              : <span className="tp-muted">—</span>}
                          </td>
                          <td data-label="Notes">
                            {r.notes.length > 0 ? (
                              <button
                                className="tp-notes-btn"
                                aria-expanded={openNotes === r.id}
                                onClick={() => setOpenNotes(openNotes === r.id ? null : r.id)}
                              >
                                {r.notes.length} {openNotes === r.id ? '▴' : '▾'}
                              </button>
                            ) : <span className="tp-muted">—</span>}
                          </td>
                          <td data-label="Last activity" className="tp-muted tp-num">
                            {r.last ? fmtInstantDate(r.last) : 'Never'}
                          </td>
                        </tr>
                        {openNotes === r.id && (
                          <tr className="tp-note-row">
                            <td colSpan={7} className="tp-full">
                              {r.notes.map((n) => (
                                <div key={n.topicId} className="tp-note-item">
                                  <div className="tp-note-t">
                                    {n.title}
                                    {n.completedAt == null && <span className="tp-muted" style={{ fontWeight: 400 }}> · not ticked</span>}
                                  </div>
                                  <div className="tp-note-b">{n.note}</div>
                                </div>
                              ))}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
                {shown.length === 0 && (
                  <div className="tp-empty">Nobody matches that filter.</div>
                )}
              </div>
            </>
          )}
        </StateView>

        <div className="tp-foot">
          Guide revision {TRAINING.revision}, reviewed {TRAINING.revised}. A tick is never removed
          automatically — when the guide changes, earlier ticks are flagged rather than cleared.
          Exports always cover everyone, not the filtered view.
        </div>
      </div>
    </>
  );
}
